import { z } from 'zod';

export const LANDING_PAGE_CONFIG_VERSION = 1;

export const LANDING_PAGE_SLUG_MIN_LENGTH = 2;
export const LANDING_PAGE_SLUG_MAX_LENGTH = 48;

/** Lowercase kebab-case: no leading, trailing or doubled hyphens. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const landingPageSlugSchema = z
  .string()
  .min(LANDING_PAGE_SLUG_MIN_LENGTH)
  .max(LANDING_PAGE_SLUG_MAX_LENGTH)
  .regex(SLUG_PATTERN, 'Use lowercase letters, numbers and single hyphens.');

export function isValidLandingPageSlug(value: unknown): value is string {
  return landingPageSlugSchema.safeParse(value).success;
}

export const HERO_MEDIA_KINDS = ['none', 'video', 'image'] as const;
export const CONTENT_MEDIA_POSITIONS = ['auto', 'left', 'right'] as const;
export const LANDING_PAGE_CTA_MODES = ['consultation', 'signup'] as const;
export const LANDING_PAGE_LOGO_SIZES = ['standard', 'large'] as const;

export type HeroMediaKind = (typeof HERO_MEDIA_KINDS)[number];
export type ContentMediaPosition = (typeof CONTENT_MEDIA_POSITIONS)[number];
export type LandingPageCtaMode = (typeof LANDING_PAGE_CTA_MODES)[number];
export type LandingPageLogoSize = (typeof LANDING_PAGE_LOGO_SIZES)[number];

export const DEFAULT_HERO_MEDIA_KIND: HeroMediaKind = 'none';
export const DEFAULT_CONTENT_MEDIA_POSITION: ContentMediaPosition = 'auto';

// `plans` and `faq` are markers with no editable content: theirs comes from the brand's
// activation flow config at render time. Every block id doubles as an in-page nav anchor.
//
// `looseObject` puts an index signature on the inferred type, so a removed or renamed field
// reads as `unknown` at every call site rather than failing to compile. Grep, don't build.
// New enum fields take `.default()`, never required: one unparseable block makes the whole
// config null, which the portal then overwrites with the starter page.
const blockSchema = z.discriminatedUnion('type', [
  z.looseObject({
    id: z.string(),
    type: z.literal('hero'),
    headline: z.string().min(1),
    subtext: z.string().optional(),
    // Three-valued so turning media off does not mean deleting the URL the user just pasted.
    mediaKind: z.enum(HERO_MEDIA_KINDS).default(DEFAULT_HERO_MEDIA_KIND),
    video: z
      .looseObject({
        mediaUrl: z.string(),
        title: z.string().optional(),
        caption: z.string().optional(),
      })
      .optional(),
    image: z.looseObject({ url: z.string() }).optional(),
  }),
  z.looseObject({
    id: z.string(),
    type: z.literal('content'),
    kicker: z.string().optional(),
    title: z.string().min(1),
    body: z.string(),
    imageUrl: z.string().optional(),
    mediaPosition: z.enum(CONTENT_MEDIA_POSITIONS).default(DEFAULT_CONTENT_MEDIA_POSITION),
  }),
  z.looseObject({ id: z.string(), type: z.literal('plans') }),
  z.looseObject({ id: z.string(), type: z.literal('faq') }),
  z.looseObject({
    id: z.string(),
    type: z.literal('closingCta'),
    headline: z.string().min(1),
    subtext: z.string().optional(),
    buttonLabel: z.string().min(1),
  }),
]);

export const landingPageConfigSchema = z.looseObject({
  version: z.number().int().positive(),
  blocks: z.array(blockSchema),
  settings: z.looseObject({
    showPricing: z.boolean(),
    cta: z.looseObject({
      mode: z.enum(LANDING_PAGE_CTA_MODES),
      consultationUrl: z.string().optional(),
    }),
    logoSize: z.enum(LANDING_PAGE_LOGO_SIZES),
    colors: z.looseObject({
      primary: z.string().optional(),
      secondary: z.string().optional(),
      accent: z.string().optional(),
    }),
  }),
});

export type LandingPageBlock = z.infer<typeof blockSchema>;
export type LandingPageBlockType = LandingPageBlock['type'];
export type LandingPageConfig = z.infer<typeof landingPageConfigSchema>;

/**
 * The starter page every brand begins from, so nobody opens a blank editor. Block ids are
 * stable readable anchors (#why-us), not uuids. Ships with no media: there is no default
 * asset to point at, so any would render broken.
 */
export const DEFAULT_LANDING_PAGE_CONFIG: LandingPageConfig = {
  version: LANDING_PAGE_CONFIG_VERSION,
  blocks: [
    {
      id: 'hero',
      type: 'hero',
      mediaKind: 'none',
      headline: 'Technology that *just works*',
      subtext:
        'Round-the-clock support for your home technology, from the team that installed it.',
    },
    {
      id: 'why-us',
      type: 'content',
      kicker: 'Why a membership',
      title: 'Support that gets ahead of the problem',
      body: 'Every membership includes 24/7 access to our remote support team. From there, choose how proactive you want us to be.',
      mediaPosition: 'right',
    },
    { id: 'plans', type: 'plans' },
    { id: 'faq', type: 'faq' },
    {
      id: 'get-started',
      type: 'closingCta',
      headline: 'Ready to get started?',
      subtext: 'Pick the plan that fits, and we will take it from there.',
      buttonLabel: 'Get started',
    },
  ],
  settings: {
    showPricing: true,
    cta: { mode: 'signup' },
    logoSize: 'standard',
    colors: {},
  },
};

export const SINGLETON_BLOCK_TYPES: ReadonlySet<LandingPageBlockType> = new Set([
  'hero',
  'plans',
  'faq',
  'closingCta',
]);

export function isSingletonBlockType(type: LandingPageBlockType): boolean {
  return SINGLETON_BLOCK_TYPES.has(type);
}

export function isValidLandingPageUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Rules the schema deliberately does not carry. Folding these into `landingPageConfigSchema`
 * would make `parseLandingPageConfig` return null for a config that breaks one, and the portal
 * reads null as "unconfigured" and offers the starter page — so a save would replace real
 * content. Callers run this after a successful parse and surface the message instead.
 */
export function findLandingPageConfigIssue(config: LandingPageConfig): string | null {
  const seenIds = new Set<string>();
  const seenSingletons = new Set<LandingPageBlockType>();

  for (const block of config.blocks) {
    if (seenIds.has(block.id)) {
      return `Duplicate block id "${block.id}". Ids are page anchors and must be unique.`;
    }
    seenIds.add(block.id);

    if (isSingletonBlockType(block.type)) {
      if (seenSingletons.has(block.type)) {
        return `A landing page can hold only one "${block.type}" block.`;
      }
      seenSingletons.add(block.type);
    }
  }

  return findInvalidUrl(config);
}

function findInvalidUrl(config: LandingPageConfig): string | null {
  const urls: (string | undefined)[] = [config.settings.cta.consultationUrl];

  for (const block of config.blocks) {
    if (block.type === 'hero') {
      urls.push(block.video?.mediaUrl, block.image?.url);
    } else if (block.type === 'content') {
      urls.push(block.imageUrl);
    }
  }

  const invalid = urls.find((url) => url !== undefined && !isValidLandingPageUrl(url));

  return invalid === undefined ? null : `"${invalid}" is not a valid http(s) URL.`;
}

/**
 * Parse a stored config. Accepts a JSON string or an already-parsed object: the DB hands back
 * a string (db-layer/connection.ts disables the JSONB type parser) while the portal holds a
 * live form object.
 *
 * Returns null rather than throwing. The column defaults to '{}', so null is the normal state
 * for a brand that has not been configured yet — not an error worth logging.
 */
export function parseLandingPageConfig(raw: unknown): LandingPageConfig | null {
  const candidate = toParseCandidate(raw);
  if (candidate === null) return null;

  const result = landingPageConfigSchema.safeParse(candidate);
  return result.success ? result.data : null;
}

function toParseCandidate(raw: unknown): unknown {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return raw;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}
